<template>
  <div class="public-site">
    <PublicSiteNav />
    <section class="hero">
      <div class="hero-media" aria-hidden="true">
        <video
          class="hero-video"
          :src="heroVideo.src"
          :poster="heroVideo.poster"
          autoplay
          muted
          loop
          playsinline
        />
      </div>
      <div class="hero-inner">
        <p class="kicker">London comedy nights</p>
        <h1>Hilarity Unlimited</h1>
        <p class="lead">
          Club nights with a real bill, a host, and comics who turn up. Come for a night out,
          or talk to us about running one in your room.
        </p>
        <div class="hero-actions">
          <a class="btn btn-primary" href="#nights">See our nights</a>
          <router-link class="btn btn-secondary" to="/for-venues">Run a night with us</router-link>
        </div>
      </div>
    </section>
    <main>

      <section id="nights" class="nights">
        <h2>Our Weekly Comedy Nights</h2>
        <div class="night-grid">
          <article v-for="night in nights" :key="night.name" class="night-card">
            <h3>{{ night.name }}</h3>
            <p class="when">{{ night.when }}</p>
            <p>
              <PublicNightLink :href="night.mapsUrl" kind="maps">{{ night.place }}</PublicNightLink>
            </p>
            <p class="area">{{ night.area }}</p>
            <p class="reserve">
              <PublicNightLink :href="night.eventbriteUrl" kind="tickets">Reserve a seat</PublicNightLink>
            </p>
          </article>
        </div>
      </section>

      <section class="split">
        <div>
          <h2>For venues</h2>
          <p>
            We book and operate the show so you can look after the bar. Repeating nights, not a
            one-off open mic.
          </p>
          <router-link class="text-link" to="/for-venues">Comedy night management →</router-link>
        </div>
        <div>
          <h2>For comedians</h2>
          <p>Apply for spots at the nights we already run.</p>
          <router-link class="text-link" to="/apply">Apply to perform →</router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
/** @see docs/APP_UI_SPEC.md — G1, G3 */
import PublicSiteNav from './PublicSiteNav.vue';
import PublicNightLink from './PublicNightLink.vue';
import { PUBLIC_HERO_VIDEO, PUBLIC_NIGHTS } from '../utils/publicSite.js';

export default {
  name: 'PublicHome',
  components: { PublicSiteNav, PublicNightLink },
  data() {
    return { nights: PUBLIC_NIGHTS, heroVideo: PUBLIC_HERO_VIDEO };
  },
};
</script>

<style scoped>
.public-site {
  min-height: 100vh;
  background: #121214;
  color: #f4efe4;
  text-align: left;
}
.hero {
  position: relative;
  overflow: hidden;
  min-height: 72vh;
  background-color: #121214;
}
.hero-media {
  position: absolute;
  inset: 0;
}
.hero-media::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(11, 11, 12, 0.42) 0%, rgba(18, 18, 20, 0.88) 100%);
}
.hero-video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.hero-inner {
  position: relative;
  z-index: 1;
  max-width: 860px;
  margin: 0 auto;
  padding: 48px 20px 56px;
}
@media (prefers-reduced-motion: reduce) {
  .hero-video {
    display: none;
  }
}
main {
  max-width: 860px;
  margin: 0 auto;
  padding: 36px 20px 64px;
}
.kicker {
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 0.75rem;
  color: #f0c14b;
  margin: 0 0 8px;
}
h1 {
  color: #f4efe4;
  text-align: left;
  font-size: 2.4rem;
  margin: 0 0 12px;
  letter-spacing: -0.03em;
}
.lead {
  font-size: 1.15rem;
  line-height: 1.5;
  max-width: 36em;
  margin: 0 0 24px;
  color: #d9d0c4;
}
.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.btn {
  display: inline-block;
  padding: 10px 16px;
  border-radius: 2px;
  text-decoration: none;
  font-weight: 600;
}
.btn-primary {
  background: #f0c14b;
  color: #1a1408;
}
.btn-secondary {
  background: transparent;
  color: #f4efe4;
  border: 1px solid #6a6458;
}
.nights h2,
.split h2 {
  color: #f4efe4;
  text-align: left;
  font-size: 1.2rem;
  margin: 0 0 16px;
}
.night-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
@media (max-width: 640px) {
  .night-grid {
    grid-template-columns: 1fr;
  }
}
.night-card {
  border: 1px solid #3a3732;
  padding: 16px;
}
.night-card h3 {
  color: #f4efe4;
  text-align: left;
  margin: 0 0 6px;
  font-size: 1.1rem;
}
.night-card p {
  margin: 0 0 4px;
  color: #d9d0c4;
}
.night-card .area {
  color: #9a9388;
  font-size: 0.9rem;
}
.night-card a {
  color: #f0c14b;
  text-decoration: none;
}
.night-card .reserve {
  margin-top: 10px;
  font-size: 0.9rem;
  font-weight: 600;
}
.split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
  margin-top: 48px;
  padding-top: 28px;
  border-top: 1px solid #3a3732;
}
@media (max-width: 640px) {
  .split {
    grid-template-columns: 1fr;
  }
}
.split p {
  color: #d9d0c4;
  margin: 0 0 10px;
  line-height: 1.45;
}
.text-link {
  color: #f0c14b;
  text-decoration: none;
  font-weight: 600;
}
</style>
